import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import CurrencyInput from 'components/field/CurrencyInput';
import IntegerInput from 'components/field/IntegerInput';
import Form from 'components/form/Form';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import useUpdateEffect from 'hooks/useUpdateEffect';

import InternationalProforma from 'pages/Shipments/PrintOrders/InternationalProforma';
import PrintAWB from 'pages/Shipments/PrintOrders/PrintAWB';
import { getConnectedPartnersList } from 'pages/ShippingPartners/ConnectedPartners/ConnectedPartnersSlice.list';
import ChargeAccountModal from 'pages/Topbar/ChargeAccount/ChargeAccountModal';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import { AUTOCOMPLETE, ORDERS, ORDER_ACTIONS, PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';
import PATHS, { mergePaths } from 'utils/paths';

import ActionColumn from './Components/ActionColumn';
import BoxInput from './Components/BoxInput';
import PriceColumn from './Components/PriceColumn';
import ResultColumn from './Components/ResultColumn';
import TotalPrice from './Components/TotalPrice';
import { addShipmentResult } from './shipmentResultsSlice';

const CreateShipmentWithMyRatesForm = (props, ref) => {
	const {
		ids,
		isReturnShipment,
		setIsAllShipmentsCreated,
		automaticShipmentMode,
		setInitialised,
		setLoading,
		setSuccessCount,
		setFailedCount,
		setTotalCount
	} = props;

	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();
	const { showError, showSuccess, showInfo } = useAlert();
	const { closeModal } = useModalRouter();

	const orders = useRef(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isAnyShipmentCreated, setIsAnyShipmentCreated] = useState(false);
	const [isAllCreated, setIsAllCreated] = useState(false);
	const [isAsyncCreateWorking, setIsAsyncCreateWorking] = useState(false);
	const { Currency } = useLocale();
	const shipmentResults = useSelector((state) => state.shipmentResults);
	const connectedPartnersList = useSelector((state) => state.connectedPartners);

	const [lastIndexOfErrorLogs, setLastIndexOfErrorLogs] = useState(null); // To retrieve error logs for shipment creation when 5 or more shipments are trying to create simultaneously.

	useUpdateEffect(() => {
		const hasActivePartner = connectedPartnersList?.some((partner) => partner.active);

		setValue('hasActivePartner', hasActivePartner);
	}, [connectedPartnersList]);

	const schema = useMemo(
		() =>
			yup.object().shape({
				items: yup.array().of(
					yup.object().shape({
						orderID: yup.string().nullable().required(),
						manualShipment: yup.boolean(),
						dcSettingsID: yup
							.mixed()
							.nullable()
							.when('manualShipment', ([manualShipment], schema) =>
								manualShipment ? schema.required() : schema
							),
						boxes: yup
							.array()
							.of(
								yup.object().shape({
									ID: yup.number().nullable(),
									width: yup.number().positive().nullable(),
									height: yup.number().positive().nullable(),
									length: yup.number().positive().nullable(),
									weight: yup.number().positive().nullable()
								})
							)
							.nullable()
					})
				)
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		getValues,
		formState: { isSubmitting }
	} = formMethods;

	const watchedItems = watch('items');

	useEffect(() => {
		setTotalCount?.(watchedItems?.length ?? 0);
	}, [watchedItems]);

	useEffect(() => {
		setLoading?.(isLoading || isSubmitting || isAsyncCreateWorking);
	}, [isLoading, isSubmitting, isAsyncCreateWorking]);

	useUpdateEffect(() => {
		const tempOrders = [...orders.current];
		let successCount = 0;
		let failedCount = 0;
		const totalCount = tempOrders.length;

		orders.current?.forEach((order, i) => {
			const result = shipmentResults[order.otoID];

			if (result) {
				if (result.shipmentNumber) {
					tempOrders[i].shipmentID = result.shipmentNumber;
					setValue(`items[${i}].shipmentID`, result.shipmentNumber);
					setValue(`items[${i}].dcSettingsID`, Number(result.dcSettingsID));
					successCount += 1;
				} else if (result.error) {
					tempOrders[i].error = result.error;
					failedCount += 1;
				}
			}
		});

		if (successCount > 0 || failedCount > 0) {
			setIsAnyShipmentCreated(true);
		}

		if (successCount + failedCount === totalCount) {
			setIsAsyncCreateWorking(false);
			dispatch(updateDynamicListsInPage());
			fetchRemainingAmount();
		}

		if (successCount === totalCount) {
			setIsAllCreated(true);
			setIsAllShipmentsCreated?.(true);
		}

		orders.current = tempOrders;
		setSuccessCount?.(successCount);
		setFailedCount?.(failedCount);
		setTotalCount?.(totalCount);
	}, [shipmentResults]);

	const fetchOrder = async () => {
		setIsLoading(true);

		const connectedPartners = await dispatch(getConnectedPartnersList()).unwrap();
		const hasActivePartner = connectedPartners?.some((partner) => partner.active);

		await axios
			.post(ORDERS.GET_ORDER_SUMMARY_BY_ID_V2, {
				orderIDs: ids?.map((id) => Number(id)),
				isReturn: isReturnShipment,
				lastIndexOfErrorLogs: lastIndexOfErrorLogs ?? undefined
			})
			.then((response) => {
				if (response?.status === 200) {
					const orderDetails = response.data?.orderDetails;

					if (!orderDetails || orderDetails.length === 0) {
						reset({
							hasActivePartner,
							items: []
						});
						showError();
						return;
					}

					orderDetails.forEach((order) => {
						if (order.error || order.shipmentNumber) {
							dispatch(
								addShipmentResult({
									[order.otoID]: {
										shipmentNumber: order.shipmentNumber,
										dcSettingsID: order.dcSettingsID ? Number(order.dcSettingsID) : undefined,
										error: order.error
									}
								})
							);
						}
					});

					orders.current = orderDetails.map((order) => ({
						...order,
						shipmentID: order.shipmentNumber ? order.shipmentNumber : undefined
					}));

					reset({
						hasActivePartner,
						items: orderDetails.map((order) => {
							const currentOrder = watchedItems?.find((item) => item.orderID === order.orderID);

							return {
								ID: order.otoID,
								orderID: order.orderID,
								manualShipment: currentOrder ? currentOrder.manualShipment : !hasActivePartner,
								dcSettingsID: order.shipmentNumber ? Number(order.dcSettingsID) : null,
								packageWeight: order.weight !== undefined ? order.weight : 1,
								customCurrency: Currency,
								boxes: order.boxes ?? null,
								shipmentID: order.shipmentNumber ? order.shipmentNumber : undefined,
								packageCount: order.packageCount ?? 1
							};
						})
					});
					setInitialised?.(true);
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	};

	useEffect(() => {
		if (ids?.length > 0) {
			fetchOrder();
		} else {
			showError({
				header: 'error',
				message: 'alert.orderNotSelected'
			});
			closeModal();
		}
	}, []);

	// if async create is working and does not complete in 15 seconds then try to fetch order again
	useUpdateEffect(() => {
		let intervalId = null;

		if (isAsyncCreateWorking) {
			intervalId = setInterval(() => {
				fetchOrder();
			}, 15000); // 15 seconds
		}

		return () => {
			clearInterval(intervalId);
		};
	}, [isAsyncCreateWorking]);

	const handleCreateShipment = async (formData) => {
		const mobileVerified = store.getState().user.companyAdminVerified;
		if (!mobileVerified) {
			showError({
				message: 'errorMsg.verifyMobileNumber',
				link: 'verifyMobileNumberButton',
				linkAction: () => {
					closeModal();
					navigate(PATHS.USER_SETTINGS);
				}
			});
			return;
		}

		await axios
			.post(ORDER_ACTIONS.CREATE_SHIPMENT, {
				items: formData.items
					.filter((item) => !item.shipmentID)
					.map((item) => ({
						...item,
						orderID: item.ID
					})),
				reverseShipment: isReturnShipment
			})
			.then((response) => {
				const { status, data } = response;

				if (status === 200) {
					if (data?.data?.type === 'async') {
						setLastIndexOfErrorLogs(data?.lastIndexOfErrorLogs);
						showInfo({
							header: 'asyncShipmentCreated',
							message: 'asyncShipmentCreatedMessage'
						});
						setIsAsyncCreateWorking(true);
					} else {
						const shipments = data?.data?.shipments;
						const hasErrorArr = [];
						const hasShipmentArr = [];
						shipments?.forEach(({ id, shipmentId, dcSettingsID, error }) => {
							if (error) {
								dispatch(
									addShipmentResult({
										[id]: {
											error
										}
									})
								);

								hasErrorArr.push(formData.items.find((item) => String(item.ID) === String(id)).ID);
							}

							if (shipmentId) {
								dispatch(
									addShipmentResult({
										[id]: {
											shipmentNumber: shipmentId,
											dcSettingsID: Number(dcSettingsID)
										}
									})
								);

								hasShipmentArr.push(formData.items.find((item) => String(item.ID) === String(id)).ID);
							}
						});

						if (hasErrorArr.length > 0) {
							showError({
								header: 'errorMsg.shipmentFailedForTheseOrders',
								persist: true,
								message: hasErrorArr.join(', '),
								link: 'shipmentErrorLogs',
								linkAction: () => {
									window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
								}
							});

							if (hasShipmentArr.length > 0) {
								showSuccess({
									header: 'successMsg.shipmentSuccessForTheseOrders',
									message: hasShipmentArr.join(', ')
								});
							}
						} else {
							showSuccess();
						}
					}
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.O004') {
					showError({
						header: 'error',
						message: 'error.O004',
						link: 'shipmentErrorLogs',
						linkAction: () => {
							window.open(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS));
						}
					});
				} else if (error.response?.data?.code === 'error.O005') {
					showError({
						header: 'error',
						message: 'error.O005'
					});
					NiceModal.show(ChargeAccountModal).then(async () => {
						const totalPrice = getValues('items')?.reduce(
							(acc, item) => acc + (item.shipmentID ? 0 : (item.price ?? 0)),
							0
						);
						const { remainingAmount } = await fetchRemainingAmount();
						if (remainingAmount >= totalPrice) {
							handleCreateShipment(formData);
						} else {
							showError({
								header: 'priceCalculator.notEnoughCreditHeader',
								message: 'priceCalculator.notEnoughCreditMessage'
							});
						}
					});
				} else if (error.response?.data.code === 'error.O022') {
					showError({
						message: 'error.orderActionNotPermittedForUser'
					});
				} else {
					AppLogger.error(error);
					showError();
				}
			});
	};

	const onSubmit = async (data) => {
		const { remainingAmount, feePerOrder } = store.getState().user;
		const totalPrice = getValues('items')?.filter((item) => !item.shipmentID).length * feePerOrder;

		const { availableFreeShipments } = await fetchRemainingAmount();

		const numberOfValidFreeShipments = availableFreeShipments?.reduce((acc, shipment) => {
			if (shipment.remainingShipments) {
				return acc + shipment.remainingShipments;
			}
			return acc;
		}, 0);

		const leftToPayShipmentsAmount = data.items.length - numberOfValidFreeShipments;

		const leftToPay = leftToPayShipmentsAmount * feePerOrder;

		if (remainingAmount < leftToPay && leftToPayShipmentsAmount > 0) {
			const { remainingAmount } = await fetchRemainingAmount();
			if (remainingAmount >= totalPrice) {
				await handleCreateShipment(data);
				return;
			}
			await NiceModal.show(ChargeAccountModal).then(async () => {
				const { remainingAmount } = await fetchRemainingAmount();
				if (remainingAmount >= totalPrice) {
					handleCreateShipment(data);
				} else {
					showError({
						header: 'priceCalculator.notEnoughCreditHeader',
						message: 'priceCalculator.notEnoughCreditMessage'
					});
				}
			});
		} else {
			await handleCreateShipment(data);
		}
	};

	const onError = (errors) => {
		AppLogger.log(errors);
		showError({
			header: 'error',
			message: 'alert.checkOrders'
		});
	};

	useImperativeHandle(
		ref,
		() => ({
			async handleCreateShipment() {
				await handleSubmit(onSubmit, onError)();
			},
			async handlePrintAWB() {
				NiceModal.show(PrintAWB, {
					ids: orders.current.map((order) => order.otoID),
					reverse: isReturnShipment
				}).then((resolved) => {
					const { internationalOrders = null } = resolved;

					if (internationalOrders?.length > 0) {
						NiceModal.show(InternationalProforma, {
							ids: internationalOrders
						});
					}
				});
			}
		}),
		[]
	);

	useEffect(() => {
		// Don't remove this useEffect and use default value of isAsyncCreateWorking in useState
		// because it will cause a bug in the shipment creation process.
		if (automaticShipmentMode) {
			setIsAsyncCreateWorking(true);
		}
	}, []);

	const columns = useMemo(() => {
		const columnsArr = [
			{
				header: 'orderID',
				accessorKey: 'orderID',
				size: 80,
				showOnMobile: true
			},
			{
				header: 'destination',
				accessorKey: isReturnShipment ? 'originCity' : 'destinationCity',
				size: 80
			},
			{
				id: 'deliveryCompany',
				header: ({ table }) =>
					table.options?.data?.length <= 1 || table.options.meta.isLoading ? (
						<Typography fontWeight="700">selectDeliveryCompany</Typography>
					) : table.options.meta.isAllCreated ||
					  table.options.meta.isSubmitting ||
					  table.options.meta.isAsyncCreateWorking ? (
						<Typography fontWeight="700">deliveryCompany</Typography>
					) : (
						<DeliveryCompanyHeader isReturnShipment={isReturnShipment} />
					),
				renderCell: ({ row, table }) => (
					<DeliveryCompanyCell
						row={row}
						isReturnShipment={isReturnShipment}
						isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
					/>
				),
				size: 120,
				showOnMobile: true
			},
			{
				id: 'shipmentNumber',
				header: 'shipmentNumber',
				renderCell: ({ row, table }) => (
					<ResultColumn
						data={row.original}
						isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
					/>
				),
				size: 150,
				maxSize: 150
			},
			{
				header: 'boxCount',
				renderCell: ({ row, table }) =>
					row.original.boxes ? (
						<BoxInput
							fieldName={`items[${row.index}]`}
							orderID={row.original.orderID}
							rowIndex={row.index}
							viewMode={
								!!row.original.shipmentID ||
								table.options.meta.isSubmitting ||
								table.options.meta.isAsyncCreateWorking
							}
							required={false}
							disabled
						/>
					) : (
						<IntegerInput
							name={`items[${row.index}].packageCount`}
							min={1}
							allowFloat
							viewMode={
								!!row.original.shipmentID ||
								table.options.meta.isSubmitting ||
								table.options.meta.isAsyncCreateWorking
							}
							decimalLimit={1}
						/>
					),
				size: 60,
				maxSize: 60
			},
			{
				header: 'boxWeight',
				accessorKey: 'weight',
				renderCell: ({ row, table }) => (
					<IntegerInput
						value={Number(
							watch(`items[${row.index}].boxes`)
								?.reduce((acc, box) => acc + box.weight, 0)
								.toFixed(1) ?? watch(`items[${row.index}].packageWeight`)
						)}
						setValue={(value) => {
							setValue(`items[${row.index}].packageWeight`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						min={0}
						unit="kg"
						allowFloat
						viewMode={
							!!row.original.shipmentID ||
							table.options.meta.isSubmitting ||
							table.options.meta.isAsyncCreateWorking
						}
						decimalLimit={1}
						disabled={row.original.boxes}
					/>
				),
				size: 100,
				maxSize: 100
			},
			{
				id: 'customValue',
				header: 'customValue',
				renderCell: ({ row, table }) => (
					<>
						{row.original.isInternationalShipment ? (
							<CurrencyInput
								name={`items[${row.index}].customValue`}
								value={watch(`items[${row.index}].customCurrency`)}
								setValue={(value) => {
									setValue(`items[${row.index}].customCurrency`, value, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
								viewMode={
									!!row.original.shipmentID ||
									table.options.meta.isSubmitting ||
									table.options.meta.isAsyncCreateWorking
								}
							/>
						) : null}
					</>
				),
				size: 100,
				maxSize: 100
			}
		];
		if (isAuthorized('ShippingAccountingModule')) {
			columnsArr.push({
				header: 'price',
				renderCell: ({ row }) => <PriceColumn index={row.index} />,
				size: 50,
				maxSize: 50,
				showOnMobile: true
			});
		}
		columnsArr.push({
			id: 'rowAction',
			header: '',
			renderCell: ({ row, table }) => (
				<ActionColumn
					row={row}
					orders={table.options?.data}
					setOrders={(data) => {
						orders.current = data;
					}}
					isReturnShipment={isReturnShipment}
					isSubmitting={table.options.meta.isSubmitting || table.options.meta.isAsyncCreateWorking}
				/>
			),
			size: 40,
			maxSize: 40,
			showOnMobile: true
		});
		return columnsArr;
	}, []);

	const visibleColumns = useMemo(
		() => ({
			shipmentNumber: isAnyShipmentCreated,
			customValue: orders.current?.some((order) => order.isInternationalShipment)
		}),
		[isAnyShipmentCreated, isLoading]
	);

	const metaData = useMemo(
		() => ({
			isAllCreated,
			isSubmitting,
			isAsyncCreateWorking,
			isLoading
		}),
		[isAllCreated, isSubmitting, isAsyncCreateWorking, isLoading]
	);

	return (
		<Form formMethods={formMethods} borderless className="mobile-no-padding">
			<Table
				data={orders.current}
				columns={columns}
				visibleColumns={visibleColumns}
				loading={isLoading}
				meta={metaData}
			/>
			{!isAllCreated && <TotalPrice withMyRates />}
		</Form>
	);
};

const DeliveryCompanyCell = ({ row, isReturnShipment, isSubmitting }) => {
	const formMethods = useFormContext();
	const { control, setValue, watch } = formMethods;
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();
	const isAuthorizedShippingPartners = useMemo(() => isAuthorized(PATHS.SHIPPING_PARTNERS), []);
	const deliveryCompaniesColumns = useMemo(
		() => [
			{
				accessor: 'logo',
				render: (logo) => <Image url={logo} alt="logo" width="25px" height="25px" />,
				width: 35
			},
			{ accessor: 'Name' }
		],
		[]
	);
	const deliveryCompaniesLabels = useMemo(() => ['Name'], []);
	const axiosPrivate = useAxiosPrivate();

	const watchedItem = useWatch({
		control,
		name: `items[${row.index}]`
	});

	const hasActivePartner = useWatch({
		control,
		name: 'hasActivePartner'
	});

	const getPrice = useCallback(
		async (order) => {
			if (!isAuthorized('ShippingAccountingModule')) {
				return;
			}
			const itemPath = `items[${row.index}]`;
			setValue(`${itemPath}.priceLoading`, true);
			try {
				if (order.dcSettingsID) {
					const { data: priceData } = await axiosPrivate.post(PRICE_CONFIGURATIONS.GET_PRICE_LIST, {
						otoId: order.ID,
						deliveryCompanySettingsId: order.dcSettingsID,
						isReverseShipment: isReturnShipment,
						withMyRates: true,
						calculationData: {
							boxes: order.boxes || []
						}
					});
					const price = priceData.data.length > 0 ? priceData.data[0]?.price : '';
					setValue(`${itemPath}.price`, price);
				} else {
					setValue(`${itemPath}.price`, '');
				}
			} catch (error) {
				AppLogger.error(error);
			} finally {
				setValue(`${itemPath}.priceLoading`, false);
			}
		},
		[row.index, isReturnShipment, setValue, isAuthorized]
	);

	return (
		<AutoComplete
			autoCompleteName={
				isReturnShipment ? AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS_RA : AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
			}
			columns={deliveryCompaniesColumns}
			setValue={(value) => {
				if (value && !watchedItem.manualShipment) {
					setValue(`items[${row.index}].manualShipment`, true);
				}
				setValue(`items[${row.index}].dcSettingsID`, value?.ID ?? null);
				getPrice(watch(`items[${row.index}]`));
			}}
			value={watchedItem.dcSettingsID}
			selectionLabelAccessor={deliveryCompaniesLabels}
			valueRemovable={false}
			name={`items[${row.index}].dcSettingsID`}
			viewMode={!!row.original.shipmentID || isSubmitting}
			customValue={hasActivePartner && !watchedItem.manualShipment ? 'automaticShipment' : undefined}
			fixedContainerWidth={false}
			firstItem={
				hasActivePartner
					? {
							action: () => {
								setValue(`items[${row.index}].manualShipment`, false);
								setValue(`items[${row.index}].dcSettingsID`, null);
								setValue(`items[${row.index}].price`, '');
							},
							label: <Typography>automaticShipment</Typography>
						}
					: isAuthorizedShippingPartners
						? {
								action: () => {
									navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION));
								},
								label: <Typography color="var(--blue)">connectShippingPartners</Typography>
							}
						: null
			}
		/>
	);
};

const DeliveryCompanyHeader = ({ isReturnShipment }) => {
	const [commonDeliveryCompany, setCommonDeliveryCompany] = useState(null);
	const navigate = useNavigate();
	const isAuthorized = useAccessControl();
	const isAuthorizedShippingPartners = useMemo(() => isAuthorized(PATHS.SHIPPING_PARTNERS), []);

	const deliveryCompaniesColumns = useMemo(
		() => [
			{
				accessor: 'logo',
				render: (logo) => <Image url={logo} alt="logo" width="25px" height="25px" />,
				width: 35
			},
			{ accessor: 'Name' }
		],
		[]
	);
	const deliveryCompaniesLabels = useMemo(() => ['Name'], []);

	const formMethods = useFormContext();
	const { control, setValue } = formMethods;

	const watchedItems = useWatch({
		control,
		name: 'items'
	});

	const hasActivePartner = useWatch({
		control,
		name: 'hasActivePartner'
	});

	return (
		<AutoComplete
			autoCompleteName={
				isReturnShipment ? AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS_RA : AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
			}
			columns={deliveryCompaniesColumns}
			setValue={(value) => {
				setCommonDeliveryCompany(value?.ID ?? null);

				setValue(
					'items',
					watchedItems.map((item) =>
						item.shipmentID || item.dcSettingsID === value?.ID
							? item
							: value?.ID
								? { ...item, manualShipment: true, dcSettingsID: value?.ID, priceLoading: true }
								: { ...item, manualShipment: false, dcSettingsID: null }
					)
				);
			}}
			value={commonDeliveryCompany}
			placeholder="selectDeliveryCompany"
			selectionLabelAccessor={deliveryCompaniesLabels}
			firstItem={
				hasActivePartner
					? null
					: isAuthorizedShippingPartners
						? {
								action: () => {
									navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION));
								},
								label: <Typography color="var(--blue)">connectShippingPartners</Typography>
							}
						: null
			}
			fixedContainerWidth={false}
		/>
	);
};
export default forwardRef(CreateShipmentWithMyRatesForm);
